import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useBrief } from "@/hooks/useBrief";
import { useProject } from "@/hooks/usePortfolio";
import {
  IT_CHECKLIST_CATEGORIES,
} from "@/components/project/BriefPanel";

/** Print-optimized rendering of a Kickstart Brief. Lives outside the
 *  AppShell so no sidebar/topbar appears. Auto-opens the print dialog
 *  so the user can "Save as PDF." */
export function BriefPrintView() {
  const { projectId } = useParams<{ projectId: string }>();
  const briefQ = useBrief(projectId ?? null);
  const projectQ = useProject(projectId ?? "");
  const brief = briefQ.data;
  const project = projectQ.data;

  // Auto-trigger print once data has loaded
  useEffect(() => {
    if (brief && project) {
      // Small delay so the page actually renders first
      const t = window.setTimeout(() => window.print(), 400);
      return () => window.clearTimeout(t);
    }
  }, [brief, project]);

  if (briefQ.isLoading || projectQ.isLoading) {
    return (
      <div className="p-10 text-sm text-ink-muted">Loading brief…</div>
    );
  }
  if (!brief || !project) {
    return (
      <div className="p-10 text-sm text-red-700">
        Couldn't load brief. {(briefQ.error as Error)?.message ?? (projectQ.error as Error)?.message}
      </div>
    );
  }

  const checklistMap = new Map<string, string>();
  for (const cat of IT_CHECKLIST_CATEGORIES) {
    for (const item of cat.items) checklistMap.set(item.key, item.label);
  }

  const totalExpected = (brief.expected_capital ?? 0) + (brief.expected_other ?? 0);
  const totalBudgeted = (brief.budgeted_capital ?? 0) + (brief.budgeted_other ?? 0);

  const anchorLabels: Record<string, string> = {
    production_ops: "Production & Ops",
    sales_distribution: "Sales & Distribution",
    people_hr: "People & HR",
    technology_systems: "Technology & Systems",
    financial: "Financial",
    market: "Market",
  };

  const statusLabels: Record<string, string> = {
    not_required: "Not Required",
    not_started: "Not Started",
    in_progress: "In Progress",
    submitted: "Submitted",
    approved: "Approved",
    declined: "Declined",
  };

  return (
    <div className="brief-print bg-surface text-ink">
      {/* Screen-only hint */}
      <div className="no-print flex items-center justify-between bg-brand-navy-600 text-white px-6 py-3 text-sm">
        <div>
          <strong>Print view:</strong> Use your browser's print dialog → Save as PDF.
        </div>
        <button
          onClick={() => window.print()}
          className="rounded bg-white/20 hover:bg-white/30 px-3 py-1 text-xs font-semibold"
        >
          Print / Save as PDF
        </button>
      </div>

      {/* Printable content */}
      <div className="mx-auto max-w-[8.5in] px-8 py-8 print:px-6 print:py-4">
        <header className="border-b border-line-strong pb-4 mb-6">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
            ETE IT PMO — Kickstart Brief
          </div>
          <h1 className="mt-1 text-2xl font-bold text-ink">{project.name}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-ink-3">
            <span className="font-mono font-semibold">{project.id}</span>
            <span>·</span>
            <span>Sponsor: {project.sponsor ?? "—"}</span>
            <span>·</span>
            <span>Status: {statusLabels[brief.status] ?? brief.status}</span>
            {brief.submitted_at && (
              <>
                <span>·</span>
                <span>Submitted {new Date(brief.submitted_at).toLocaleDateString()}</span>
              </>
            )}
          </div>
        </header>

        <PrintSection title="Basic Info">
          <Row label="Requester & Department" value={brief.requester_name} />
          <Row label="Requester Department Detail" value={brief.requester_department} />
          <Row label="Primary Contact" value={brief.primary_contact} />
        </PrintSection>

        <PrintSection title="Expected Cost vs Budget">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line-strong">
                <th className="text-left py-2 font-semibold">Category</th>
                <th className="text-right py-2 font-semibold">Expected Cost</th>
                <th className="text-right py-2 font-semibold">$s Approved in Annual Budget</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-line-soft">
                <td className="py-2">Capital Investment</td>
                <td className="py-2 text-right tabular-nums">{fmt(brief.expected_capital)}</td>
                <td className="py-2 text-right tabular-nums">{fmt(brief.budgeted_capital)}</td>
              </tr>
              <tr className="border-b border-line-soft">
                <td className="py-2">Other Expenses</td>
                <td className="py-2 text-right tabular-nums">{fmt(brief.expected_other)}</td>
                <td className="py-2 text-right tabular-nums">{fmt(brief.budgeted_other)}</td>
              </tr>
              <tr className="border-t border-line-strong font-semibold">
                <td className="py-2">Total</td>
                <td className="py-2 text-right tabular-nums">{fmt(totalExpected)}</td>
                <td className="py-2 text-right tabular-nums">{fmt(totalBudgeted)}</td>
              </tr>
            </tbody>
          </table>
        </PrintSection>

        <PrintSection title="Purpose & Alignment">
          <Block label="Business Need" value={brief.business_need} />
          <Row
            label="Strategic Anchor"
            value={brief.strategic_anchor ? anchorLabels[brief.strategic_anchor] ?? brief.strategic_anchor : null}
          />
          <Block label="Target KPI / Outcome" value={brief.target_kpi} />
        </PrintSection>

        <PrintSection title="Scope & Success Criteria">
          <Block label="In Scope" value={brief.in_scope} />
          <Block label="Out of Scope" value={brief.out_of_scope} />
          <Block label="Success Criteria" value={brief.success_criteria} />
        </PrintSection>

        <PrintSection title="Stakeholders">
          <Row label="Business Owner" value={brief.business_owner} />
          <Block label="Key Departments / Partners" value={brief.key_departments} />
        </PrintSection>

        <PrintSection title="Timeline">
          <Row label="Expected Start" value={brief.expected_start} />
          <Block label="Key Milestones" value={brief.key_milestones} />
        </PrintSection>

        <PrintSection title="Risks & Dependencies">
          <Block label="Top Risks" value={brief.top_risks} />
          <Block label="Dependencies" value={brief.dependencies} />
        </PrintSection>

        <PrintSection title="IT Involvement">
          <Row
            label="IT Needed"
            value={
              brief.it_needed === "yes"
                ? "Yes"
                : brief.it_needed === "no"
                  ? "No"
                  : brief.it_needed === "unknown"
                    ? "I don't know"
                    : null
            }
          />
          <Row label="T-Shirt Size" value={brief.tshirt_size} />
          <Row label="Cost Range" value={brief.cost_range} />
        </PrintSection>

        <PrintSection title="IT Impact Checklist">
          {brief.it_checklist.length === 0 ? (
            <div className="text-sm text-ink-muted italic">No items selected.</div>
          ) : (
            <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm">
              {brief.it_checklist.map((key) => (
                <div key={key} className="flex items-start gap-2">
                  <span>☒</span>
                  <span>{checklistMap.get(key) ?? key}</span>
                </div>
              ))}
            </div>
          )}
        </PrintSection>

        <PrintSection title="Other Information">
          <Block label="Other Relevant Info" value={brief.other_info} />
          <Row label="Available Dates for Intake Discussion" value={brief.intake_discussion_dates} />
        </PrintSection>

        {brief.status === "declined" && brief.decline_reason && (
          <PrintSection title="Decision">
            <Block label="Decline Reason" value={brief.decline_reason} />
            {brief.decided_by && <Row label="Declined By" value={brief.decided_by} />}
            {brief.decided_at && <Row label="Declined On" value={new Date(brief.decided_at).toLocaleDateString()} />}
          </PrintSection>
        )}

        <footer className="mt-8 pt-4 border-t border-line text-[10px] text-ink-subtle">
          Generated from Resource Planner · {new Date().toLocaleString()}
        </footer>
      </div>
    </div>
  );
}

function PrintSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-6 break-inside-avoid-page">
      <h2 className="text-sm font-bold uppercase tracking-wider text-ink-2 border-b border-line pb-1 mb-2">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Row({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex gap-3 py-1.5 text-sm">
      <div className="w-56 shrink-0 text-ink-muted text-xs font-semibold uppercase tracking-wider">
        {label}
      </div>
      <div className="flex-1 text-ink">{value?.trim() ? value : <span className="text-ink-subtle">—</span>}</div>
    </div>
  );
}

function Block({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="py-2 text-sm">
      <div className="text-xs font-semibold uppercase tracking-wider text-ink-muted mb-1">
        {label}
      </div>
      <div className="whitespace-pre-wrap text-ink">
        {value?.trim() ? value : <span className="text-ink-subtle">—</span>}
      </div>
    </div>
  );
}

function fmt(n: number | null | undefined): string {
  if (!n) return "$0";
  return `$${Math.round(n).toLocaleString()}`;
}
